import { useState } from 'react'
import { Button, Group, Modal, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { chamadosApi } from '@/api/chamados'
import { formatApiError } from '@/api/errors'
import type { ChamadoParaAtendimentoIa } from './IniciarAtendimentoIaButton'

type Pedido = { id: number; estado: string }

/** O 409 `pedido_em_andamento` da rota: o pedido já está no repositório (spec §5). */
function pedidosDoConflito(err: unknown): Pedido[] | null {
  if (!isAxiosError(err) || err.response?.status !== 409) return null
  const detail = err.response.data?.detail
  if (detail?.codigo !== 'pedido_em_andamento' || !Array.isArray(detail.pedidos)) return null
  return detail.pedidos as Pedido[]
}

/**
 * "Encerrar atendimento com IA" (spec 2026-10-16 §4.2 e §4.3, decisão D2). A conversa
 * fica para leitura e o Iniciar a reabre; os pedidos de Terraform que ainda não
 * tocaram o repositório são cancelados. Quem barra é a rota (403/409).
 */
export function EncerrarAtendimentoIaModal({
  chamado,
  opened,
  onClose,
}: {
  chamado: ChamadoParaAtendimentoIa
  opened: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [enviando, setEnviando] = useState(false)
  const pedidos = chamado.atendimento_ia?.pedidos_em_andamento ?? []
  const cancelaveis = pedidos.filter((p) => p.cancelavel !== false)
  const bloqueiam = pedidos.filter((p) => p.cancelavel === false)

  async function encerrar() {
    if (enviando) return
    setEnviando(true)
    try {
      const { data } = await chamadosApi.encerrarAtendimentoIa(chamado.id)
      const cancelados = data.pedidos_cancelados.length
        ? ` Pedidos cancelados: ${data.pedidos_cancelados.map((i) => `#${i}`).join(', ')}.`
        : ''
      const assumir = data.atribuicao === 'recusada'
        ? ` E não foi possível assumir o chamado: ${data.atribuicao_motivo ?? 'sem motivo informado'}.`
        : ''
      notifications.show({
        color: data.atribuicao === 'recusada' ? 'yellow' : 'teal',
        title: 'Atendimento encerrado',
        message: `A conversa continua no chamado, para leitura.${cancelados}${assumir}`,
      })
      await queryClient.invalidateQueries({ queryKey: ['chamado', String(chamado.id)] })
      onClose()
    } catch (err) {
      const conflito = pedidosDoConflito(err)
      const p = conflito?.[0]
      notifications.show({
        color: 'red',
        title: 'Não foi possível encerrar o atendimento',
        message: p
          ? `O pedido #${p.id} já está no repositório (${p.estado}). Encerre depois que ele terminar, ou peça o saneamento.`
          : formatApiError(err, 'Falha ao encerrar o atendimento com IA.'),
      })
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Encerrar atendimento com IA">
      <Stack gap="sm">
        <Text size="sm">A conversa continua no chamado, para leitura. Você pode reabrir depois.</Text>
        {bloqueiam.length > 0 ? (
          <Text size="sm" c="red">
            O pedido #{bloqueiam[0].id} já está no repositório ({bloqueiam[0].estado}). Encerre depois
            que ele terminar, ou peça o saneamento.
          </Text>
        ) : cancelaveis.length > 0 && (
          <Text size="sm">
            Estes pedidos de Terraform serão cancelados:{' '}
            {cancelaveis.map((p) => `#${p.id} (${p.estado})`).join(', ')}
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={enviando}>
            Cancelar
          </Button>
          <Button color="red" onClick={encerrar} loading={enviando} disabled={bloqueiam.length > 0}>
            Encerrar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
